import { type FC, useCallback } from 'react';
import { useToggle } from '@direct-frontend/stdlib';
import { type VariantInfo } from '@/shared/api/variants';
import type { OrderProductProps } from '@/shared/components/OrderProduct';
import { SizeTable } from '@/shared/components/SizeTable';
import { ANIMATION_STATES, type AnimationState } from '@/shared/lib/animationState';
import { useIsDesktop } from '@/shared/lib/hooks';
import { useCurrentVariants } from '../../lib/useCurrentVariants';
import { useVariantsController } from '../../lib/useVariantsController';
import { OrderVariantsEditButton } from '../OrderVariantsEditButton';
import { OrderVariantsEditModal } from '../OrderVariantsEditModal';

interface OrderVariantsEditProps extends Pick<OrderProductProps, 'characteristics'> {
    chunkId: number;
    variantId: string;
}

export const OrderVariantsEdit: FC<OrderVariantsEditProps> = ({ characteristics, chunkId, variantId }) => {
    const { isDesktop } = useIsDesktop();

    const { isOpen: isModalOpen, setTruthy: openModal, setFalsy: closeModal } = useToggle();
    const { isOpen: isSizeTableOpen, setTruthy: openSizeTableModal, setFalsy: closeSizeTableModal } = useToggle();

    const { fetchVariants, updateVariants, currentVariants, resetVariants, isLoading } = useVariantsController({
        chunkId,
        variantId,
    });
    const { sizeTableSrc, newVariantId, variantsInfo } = useCurrentVariants(currentVariants);

    const isInitialLoading = isLoading && variantsInfo.length === 0;
    const stubLength = isInitialLoading ? characteristics.length : 0;

    const handleOpenVariantsModal = useCallback(async () => {
        openModal();

        try {
            await fetchVariants();
        } catch {
            return;
        }
    }, [fetchVariants, openModal]);

    const handleChangeVariants = useCallback(
        async (variant: VariantInfo) => {
            try {
                await fetchVariants({
                    selectedId: variant.id,
                    selectedValue: variant.selected_value,
                });
            } catch {
                return;
            }
        },
        [fetchVariants]
    );

    const handleSaveVariant = useCallback(async () => {
        if (!newVariantId || newVariantId === variantId) {
            closeModal();

            return;
        }

        try {
            await updateVariants({
                newVariantId,
            });
        } catch {
            return;
        }

        closeModal();
    }, [closeModal, newVariantId, updateVariants, variantId]);

    const handleOpenSizeTableModal = useCallback(() => {
        /**
         * На десктопе при открытии модалки с таблицей закрываем модалку с выбором вариантов
         * при закрытии модалки с таблицей обратно открываем модалку с выбором вариантов
         * На тачах открываем дровер с таблицой поверх дровера с выбором вариантов
         */
        if (isDesktop) {
            closeModal();
        }

        openSizeTableModal();
    }, [isDesktop, closeModal, openSizeTableModal]);

    const handleCloseSizeTableModal = useCallback(() => {
        closeSizeTableModal();
        /**
         * При закрытии модалки с таблицей обратно открываем модалку с выбором вариантов
         */
        openModal();
    }, [closeSizeTableModal, openModal]);

    const handleAnimationEndModal = useCallback(
        (animationState: AnimationState) => {
            if (animationState === ANIMATION_STATES.CLOSE && !isSizeTableOpen) {
                resetVariants();
            }
        },
        [isSizeTableOpen, resetVariants]
    );

    if (!characteristics.length) {
        return null;
    }

    return (
        <>
            <OrderVariantsEditButton characteristics={characteristics} onClick={handleOpenVariantsModal} />

            <OrderVariantsEditModal
                isOpen={isModalOpen}
                stubLength={stubLength}
                onClose={closeModal}
                sizeTableSrc={sizeTableSrc}
                characteristics={variantsInfo}
                handleCharacteristicsChange={handleChangeVariants}
                onSizeTableOpen={handleOpenSizeTableModal}
                onSaveVariant={handleSaveVariant}
                onAnimationEnd={handleAnimationEndModal}
            />

            {sizeTableSrc && (
                <SizeTable sizeTableSrc={sizeTableSrc} isOpen={isSizeTableOpen} onClose={handleCloseSizeTableModal} />
            )}
        </>
    );
};
